"use client";

import { useState, FormEvent } from "react";
import {
  useStripe,
  useElements,
  PaymentElement,
  Elements,
} from "@stripe/react-stripe-js";

import { X, Lock, Sparkles, CheckCircle, AlertCircle, Tag } from "lucide-react";
import { getStripe } from "@/lib/stripe/client";
import { createClient } from "@/lib/supabase/client";

interface PaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (email: string) => void;
}

interface CustomerFormData {
  name: string;
  email: string;
}

// Inner form that uses Stripe hooks
function CheckoutForm({
  clientSecret,
  onSuccess,
  onError,
}: {
  clientSecret: string;
  onSuccess: () => void;
  onError: (msg: string) => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setIsProcessing(true);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/report-generator`,
      },
      redirect: "if_required",
    });

    if (error) {
      console.error("Stripe confirm error:", error);
      onError(error.message ?? "Payment failed. Please try again.");
      setIsProcessing(false);
      return;
    }

    if (paymentIntent?.status === "succeeded") {
      // Confirm on backend to update DB record
      const supabase = createClient();
      const { error: confirmError } = await supabase.functions.invoke(
        "confirm-payment",
        { body: { paymentIntentId: paymentIntent.id } }
      );
      if (confirmError) {
        console.error("Confirm payment backend error:", confirmError);
      }
      onSuccess();
    }

    setIsProcessing(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div
        className="rounded-xl p-4"
        style={{
          background: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(219,184,77,0.15)",
        }}
      >
        <PaymentElement
          options={{
            layout: "tabs",
            appearance: {
              theme: "night",
              variables: {
                colorPrimary: "#dbb84d",
                colorBackground: "rgba(40,20,50,0.95)",
                colorText: "#f0e8f5",
                colorDanger: "#ef4444",
                fontFamily: "'DM Sans', sans-serif",
                borderRadius: "10px",
              },
            },
          }}
        />
      </div>

      <button
        type="submit"
        disabled={!stripe || isProcessing}
        className="w-full flex items-center justify-center gap-3 py-4 rounded-xl font-semibold text-base transition-all"
        style={{
          background: isProcessing
            ? "rgba(219,184,77,0.5)"
            : "linear-gradient(135deg, #dbb84d 0%, #c9a43a 100%)",
          color: "#1a0a22",
          cursor: isProcessing ? "not-allowed" : "pointer",
          boxShadow: isProcessing
            ? "none" :"0 4px 20px rgba(219,184,77,0.35)",
        }}
      >
        {isProcessing ? (
          <>
            <svg
              className="animate-spin w-5 h-5"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
              />
            </svg>
            <span>Processing Payment...</span>
          </>
        ) : (
          <>
            <Lock className="w-5 h-5" />
            <span>Pay $22.00 Securely</span>
          </>
        )}
      </button>

      <p className="text-center text-xs text-white/40" style={{ fontFamily: "'DM Sans', sans-serif" }}>
        <Lock className="inline w-3 h-3 mr-1 mb-0.5" />
        Secured by Stripe · 256-bit SSL encryption
      </p>
    </form>
  );
}

const stripePromise = getStripe();

export default function PaymentModal({
  isOpen,
  onClose,
  onSuccess,
}: PaymentModalProps) {
  const [step, setStep] = useState<"info" | "payment" | "success" | "already_used">("info");
  const [customerData, setCustomerData] = useState<CustomerFormData>({
    name: "",
    email: "",
  });
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [promoCode, setPromoCode] = useState("");
  const [promoApplied, setPromoApplied] = useState(false);

  const VALID_PROMO = "QUANTUMEGIFT";

  const handlePromoChange = (value: string) => {
    const upper = value.toUpperCase();
    setPromoCode(upper);
    setPromoApplied(upper === VALID_PROMO);
  };

  const handleInfoSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!customerData.name.trim() || !customerData.email.trim()) return;
    setIsCreating(true);
    setIsCheckingEmail(true);
    setErrorMsg(null);

    try {
      const supabase = createClient();

      // Check if this email already has a succeeded payment with report already generated
      const { data: existingPayments, error: checkError } = await supabase
        .from("nautilus_payments")
        .select("id, payment_status, report_generated")
        .eq("customer_email", customerData.email.trim().toLowerCase())
        .eq("payment_status", "succeeded")
        .order("created_at", { ascending: false })
        .limit(1);

      setIsCheckingEmail(false);

      if (checkError) {
        console.error("Email check error:", checkError);
        // Non-blocking — proceed to payment if check fails
      } else if (existingPayments && existingPayments.length > 0) {
        const latestPayment = existingPayments[0];
        if (latestPayment.report_generated) {
          setStep("already_used");
          setIsCreating(false);
          return;
        } else {
          onSuccess(customerData.email.trim().toLowerCase());
          handleClose();
          return;
        }
      }

      // Promo code path — insert a free succeeded record and unlock
      if (promoApplied) {
        const { error: insertError } = await supabase
          .from("nautilus_payments")
          .insert({
            customer_name: customerData.name.trim(),
            customer_email: customerData.email.trim().toLowerCase(),
            amount: 0,
            currency: "usd",
            payment_status: "succeeded",
            report_generated: false,
            description: "QuantuMe Avatar Blueprint Report (Promo: QUANTUMEGIFT)",
          });

        if (insertError) {
          console.error("Promo insert error:", insertError);
          // Non-blocking — still unlock
        }

        setStep("success");
        setTimeout(() => {
          onSuccess(customerData.email.trim().toLowerCase());
          handleClose();
        }, 2500);
        return;
      }

      // No existing payment — proceed to create payment intent
      const { data, error } = await supabase.functions.invoke<{
        clientSecret: string;
        paymentIntentId: string;
        recordId: string;
      }>("create-payment-intent", {
        body: {
          paymentData: {
            amount: 22,
            currency: "usd",
            description: "QuantuMe Avatar Blueprint Report",
            promotekit_referral: (typeof window !== "undefined" && (window as any).promotekit_referral) || undefined,
          },
          customerInfo: {
            userId: null,
            name: customerData.name.trim(),
            email: customerData.email.trim().toLowerCase(),
            stripeCustomerId: null,
          },
        },
      });

      if (error) {
        console.error("Create payment intent error:", error);
        throw new Error(
          (data as { error?: string })?.error ?? error.message ?? "Payment setup failed"
        );
      }

      if (!data?.clientSecret) {
        throw new Error("No client secret returned from payment setup.");
      }

      setClientSecret(data.clientSecret);
      setStep("payment");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Payment setup failed. Please try again.";
      setErrorMsg(message);
    } finally {
      setIsCreating(false);
      setIsCheckingEmail(false);
    }
  };

  const handlePaymentSuccess = () => {
    setStep("success");
    setTimeout(() => {
      onSuccess(customerData.email.trim().toLowerCase());
      handleClose();
    }, 2500);
  };

  const handlePaymentError = (msg: string) => {
    setErrorMsg(msg);
  };

  const handleClose = () => {
    setStep("info");
    setCustomerData({ name: "", email: "" });
    setClientSecret(null);
    setErrorMsg(null);
    setPromoCode("");
    setPromoApplied(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center z-50 px-4"
      style={{ background: "rgba(10,4,18,0.85)", backdropFilter: "blur(8px)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        className="relative w-full max-w-md rounded-2xl p-8"
        style={{
          background:
            "linear-gradient(135deg, rgba(54,23,64,0.98) 0%, rgba(30,10,45,0.98) 100%)",
          border: "1px solid rgba(219,184,77,0.25)",
          boxShadow:
            "0 24px 64px rgba(0,0,0,0.6), inset 0 1px 0 rgba(219,184,77,0.15)",
          maxHeight: "90vh",
          overflowY: "auto",
        }}
      >
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 p-2 rounded-lg transition-colors"
          style={{
            color: "rgba(255,255,255,0.4)",
            background: "rgba(255,255,255,0.05)",
          }}
          aria-label="Close payment modal"
        >
          <X className="w-4 h-4" />
        </button>

        {/* Success State */}
        {step === "success" && (
          <div className="text-center py-8 fade-in">
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
              style={{ background: "rgba(34,197,94,0.15)", border: "1px solid rgba(34,197,94,0.3)" }}
            >
              <CheckCircle className="w-8 h-8" style={{ color: "#22c55e" }} />
            </div>
            <h3
              className="text-2xl font-bold mb-2"
              style={{ color: "#dbb84d", fontFamily: "'Abril Fatface', cursive" }}
            >
              {promoApplied ? "Blueprint Unlocked!" : "Payment Successful!"}
            </h3>
            <p className="text-white/60 text-sm" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Your Avatar Blueprint is now unlocked. Preparing your form…
            </p>
          </div>
        )}

        {/* Already Used State */}
        {step === "already_used" && (
          <div className="text-center py-8 fade-in">
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-6"
              style={{ background: "rgba(239,68,68,0.12)", border: "1px solid rgba(239,68,68,0.3)" }}
            >
              <AlertCircle className="w-8 h-8" style={{ color: "#ef4444" }} />
            </div>
            <h3
              className="text-2xl font-bold mb-3"
              style={{ color: "#dbb84d", fontFamily: "'Abril Fatface', cursive" }}
            >
              Report Already Generated
            </h3>
            <p
              className="text-white/65 text-sm leading-relaxed mb-6"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              A report has already been generated for{" "}
              <span style={{ color: "#dbb84d" }}>{customerData.email}</span>.
              Each purchase allows one report generation.
            </p>
            <p
              className="text-white/45 text-xs leading-relaxed mb-6"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              If you believe this is an error or need assistance, please contact us.
            </p>
            <button
              onClick={handleClose}
              className="inline-flex items-center justify-center px-8 py-3 rounded-xl font-semibold text-sm transition-all"
              style={{
                background: "rgba(219,184,77,0.12)",
                border: "1px solid rgba(219,184,77,0.3)",
                color: "#dbb84d",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Close
            </button>
          </div>
        )}

        {/* Info Collection Step */}
        {step === "info" && (
          <>
            <div className="text-center mb-6">
              <div
                className="inline-flex items-center justify-center w-12 h-12 rounded-xl mb-4"
                style={{
                  background: "rgba(219,184,77,0.12)",
                  border: "1px solid rgba(219,184,77,0.25)",
                }}
              >
                <Sparkles className="w-6 h-6" style={{ color: "#dbb84d" }} />
              </div>
              <h2
                className="text-2xl font-bold mb-1"
                style={{
                  color: "#dbb84d",
                  fontFamily: "'Abril Fatface', cursive",
                }}
              >
                Unlock Your Blueprint
              </h2>
              <p
                className="text-sm text-white/55"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                {promoApplied ? (
                  <span style={{ color: "#22c55e", fontWeight: 600 }}>🎉 100% off applied!</span>
                ) : (
                  "One-time payment · $22.00 USD"
                )}
              </p>
            </div>

            {/* What's included */}
            <div
              className="rounded-xl p-4 mb-6 space-y-2"
              style={{
                background: "rgba(219,184,77,0.06)",
                border: "1px solid rgba(219,184,77,0.12)",
              }}
            >
              {[
                "Personalized Avatar Blueprint Report",
                "Your Archetypal Essence & Identity",
                "30-Day Alignment Action Plan",
              ].map((item) => (
                <div key={item} className="flex items-center gap-2">
                  <CheckCircle
                    className="w-4 h-4 flex-shrink-0"
                    style={{ color: "rgba(219,184,77,0.7)" }}
                  />
                  <span
                    className="text-sm text-white/65"
                    style={{ fontFamily: "'DM Sans', sans-serif" }}
                  >
                    {item}
                  </span>
                </div>
              ))}
            </div>

            <form onSubmit={handleInfoSubmit} className="space-y-4">
              <div>
                <label
                  className="block text-sm font-medium mb-1.5 text-white/75"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  Full Name
                </label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Amara Osei"
                  value={customerData.name}
                  onChange={(e) =>
                    setCustomerData((p) => ({ ...p, name: e.target.value }))
                  }
                  className="form-input w-full"
                />
              </div>
              <div>
                <label
                  className="block text-sm font-medium mb-1.5 text-white/75"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  Email Address
                </label>
                <input
                  type="email"
                  required
                  placeholder="you@example.com"
                  value={customerData.email}
                  onChange={(e) =>
                    setCustomerData((p) => ({ ...p, email: e.target.value }))
                  }
                  className="form-input w-full"
                />
              </div>

              {/* Promo Code Field */}
              <div>
                <label
                  className="block text-sm font-medium mb-1.5 text-white/75"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  Promo Code <span className="text-white/35 font-normal">(optional)</span>
                </label>
                <div className="relative">
                  <Tag
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
                    style={{ color: promoApplied ? "#22c55e" : "rgba(255,255,255,0.3)" }}
                  />
                  <input
                    type="text"
                    placeholder="Enter promo code"
                    value={promoCode}
                    onChange={(e) => handlePromoChange(e.target.value)}
                    className="form-input w-full"
                    style={{
                      paddingLeft: "2.25rem",
                      paddingRight: promoApplied ? "7rem" : "1rem",
                      borderColor: promoApplied
                        ? "rgba(34,197,94,0.5)"
                        : promoCode && promoCode !== VALID_PROMO
                        ? "rgba(239,68,68,0.4)"
                        : undefined,
                    }}
                  />
                  {promoApplied && (
                    <span
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-bold px-2 py-0.5 rounded-full"
                      style={{
                        background: "rgba(34,197,94,0.15)",
                        color: "#22c55e",
                        border: "1px solid rgba(34,197,94,0.35)",
                        fontFamily: "'DM Sans', sans-serif",
                      }}
                    >
                      100% OFF
                    </span>
                  )}
                </div>
                {promoApplied && (
                  <p
                    className="mt-1.5 text-xs flex items-center gap-1"
                    style={{ color: "#22c55e", fontFamily: "'DM Sans', sans-serif" }}
                  >
                    <CheckCircle className="w-3.5 h-3.5" />
                    Promo code applied — your Blueprint is free!
                  </p>
                )}
                {promoCode && !promoApplied && (
                  <p
                    className="mt-1.5 text-xs"
                    style={{ color: "rgba(239,68,68,0.8)", fontFamily: "'DM Sans', sans-serif" }}
                  >
                    Invalid promo code
                  </p>
                )}
              </div>

              {errorMsg && (
                <p
                  className="text-sm rounded-lg px-4 py-3"
                  style={{
                    color: "rgba(239,68,68,0.9)",
                    background: "rgba(239,68,68,0.08)",
                    border: "1px solid rgba(239,68,68,0.2)",
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  {errorMsg}
                </p>
              )}

              <button
                type="submit"
                disabled={isCreating}
                className="w-full flex items-center justify-center gap-3 py-4 rounded-xl font-semibold text-base transition-all mt-2"
                style={{
                  background: isCreating
                    ? "rgba(219,184,77,0.5)"
                    : promoApplied
                    ? "linear-gradient(135deg, #22c55e 0%, #16a34a 100%)"
                    : "linear-gradient(135deg, #dbb84d 0%, #c9a43a 100%)",
                  color: "#1a0a22",
                  cursor: isCreating ? "not-allowed" : "pointer",
                  boxShadow: isCreating
                    ? "none"
                    : promoApplied
                    ? "0 4px 20px rgba(34,197,94,0.35)"
                    : "0 4px 20px rgba(219,184,77,0.35)",
                }}
              >
                {isCreating ? (
                  <>
                    <svg
                      className="animate-spin w-5 h-5"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      />
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                      />
                    </svg>
                    <span>{isCheckingEmail ? "Checking account…" : "Setting up…"}</span>
                  </>
                ) : promoApplied ? (
                  <>
                    <Sparkles className="w-5 h-5" />
                    <span>Unlock Free Blueprint →</span>
                  </>
                ) : (
                  <>
                    <Lock className="w-5 h-5" />
                    <span>Continue to Payment</span>
                  </>
                )}
              </button>
            </form>
          </>
        )}

        {/* Payment Step */}
        {step === "payment" && clientSecret && (
          <>
            <div className="text-center mb-6">
              <h2
                className="text-2xl font-bold mb-1"
                style={{
                  color: "#dbb84d",
                  fontFamily: "'Abril Fatface', cursive",
                }}
              >
                Complete Payment
              </h2>
              <p
                className="text-sm text-white/55"
                style={{ fontFamily: "'DM Sans', sans-serif" }}
              >
                Avatar Blueprint · $22.00 USD
              </p>
            </div>

            {errorMsg && (
              <p
                className="text-sm rounded-lg px-4 py-3 mb-4"
                style={{
                  color: "rgba(239,68,68,0.9)",
                  background: "rgba(239,68,68,0.08)",
                  border: "1px solid rgba(239,68,68,0.2)",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {errorMsg}
              </p>
            )}

            <Elements
              stripe={stripePromise}
              options={{
                clientSecret,
                appearance: {
                  theme: "night",
                  variables: {
                    colorPrimary: "#dbb84d",
                    colorBackground: "rgba(40,20,50,0.95)",
                    colorText: "#f0e8f5",
                    colorDanger: "#ef4444",
                    fontFamily: "'DM Sans', sans-serif",
                    borderRadius: "10px",
                  },
                },
              }}
            >
              <CheckoutForm
                clientSecret={clientSecret}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
              />
            </Elements>

            <button
              onClick={() => {
                setStep("info");
                setErrorMsg(null);
              }}
              className="mt-4 w-full text-center text-sm text-white/40 hover:text-white/60 transition-colors"
              style={{ fontFamily: "'DM Sans', sans-serif" }}
            >
              ← Back
            </button>
          </>
        )}
      </div>
    </div>
  );
}
